// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file FileInput.tsx
 * @input Uses React, useId, Field, Icon, Spinner, VisuallyHidden
 * @output Exports FileInput component, FileInputProps, FileInputStatus
 * @position Core implementation; consumed by index.ts, tested by FileInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/FileInput/FileInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/FileInput/FileInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/FileInput/index.ts (exports if types change)
 * - /apps/storybook/stories/FileInput.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/FileInput/ (showcase blocks)
 */
import { useId, useCallback, useRef, useState, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, spacingVars, radiusVars, typographyVars, typeScaleVars, borderVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { Field, InputClearButton } from "../Field/index.js";
import { Icon } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import { useInputStatusIcon } from "../hooks/useInputStatusIcon.js";
import { useClickableContainer } from "../hooks/useClickableContainer.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function formatFileSize(bytes) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function validateFiles(files, accept, maxSize, maxFiles, isMultiple) {
  const errors = [];
  let valid = files;
  if (accept) {
    const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    valid = valid.filter(file => {
      const matches = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type);
        }
        if (type.endsWith('/*')) {
          return file.type.startsWith(type.slice(0, -1));
        }
        return file.type.toLowerCase() === type;
      });
      if (!matches) {
        errors.push(`"${file.name}" is not an accepted file type`);
      }
      return matches;
    });
  }
  if (maxSize != null) {
    valid = valid.filter(file => {
      if (file.size > maxSize) {
        errors.push(`"${file.name}" exceeds ${formatFileSize(maxSize)} limit`);
        return false;
      }
      return true;
    });
  }
  if (isMultiple && maxFiles != null && valid.length > maxFiles) {
    errors.push(`Maximum ${maxFiles} files allowed`);
    valid = valid.slice(0, maxFiles);
  }
  return {
    valid,
    errors
  };
}
const styles = {
  dropzone: {
    kB7OPa: "x9f619",
    kVAEAm: "x1n2onr6",
    kY2c9j: "x1vjfegm",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x1txdalj",
    k8WAf4: "xq6koh6",
    kg3NbH: "x1pzlopt",
    kMzoRj: "x1litavf",
    ksu8eU: "xbsl7fq",
    kVAM5u: "xvy26l8 x6q1khz",
    kaIpWk: "xh6dtrn",
    kWkggS: "x10xzikg",
    k1ekBW: "x1tv3a4w",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  dropzoneHover: {
    kGVxlE: "xw6ruzt",
    $$css: true
  },
  dropzoneActive: {
    kVAM5u: "xad5do",
    kWkggS: "xgcxg3y",
    $$css: true
  },
  dropzoneDisabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    kVAM5u: "xvy26l8",
    $$css: true
  },
  compact: {
    kB7OPa: "x9f619",
    kVAEAm: "x1n2onr6",
    kY2c9j: "x1vjfegm",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xvy26l8 x6q1khz",
    kaIpWk: "xh6dtrn",
    kWkggS: "x10xzikg",
    k1ekBW: "x12zzom9",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    kGVxlE: "x1gnnqk1 x70dsy8",
    kkrTdU: "x1ypdohk",
    kZKoxP: "x1ueg155",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  compactDisabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    kVAM5u: "xvy26l8",
    $$css: true
  }
};
const statusBorderStyles = {
  warning: {
    kVAM5u: "x8wg1ba",
    $$css: true
  },
  error: {
    kVAM5u: "x1ofxpqo",
    $$css: true
  },
  success: {
    kVAM5u: "x16m2moy",
    $$css: true
  }
};
/**
 * A file input component with optional drag-and-drop support.
 *
 * @example
 * ```
 * <FileInput label="Resume" value={file} onChange={setFile} accept=".pdf" />
 * ```
 */
export function FileInput({
  label,
  isLabelHidden = false,
  value,
  onChange,
  changeAction,
  accept,
  isMultiple = false,
  maxSize,
  maxFiles,
  isDisabled = false,
  disabledMessage,
  isRequired = false,
  isLoading = false,
  status: statusProp,
  statusVariant = 'attached',
  description,
  placeholder,
  mode = 'input',
  isOptional = false,
  labelTooltip,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const requiredID = useId();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [, startTransition] = useTransition();

  // Announce successful file selection to screen readers via a persistent
  // live region (forms-17). Validation errors are announced (assertively) by
  // the FieldStatus that the derived error status mounts, so only the
  // successful attach needs an explicit announcement here.
  const announce = useAnnounce();

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the visible container (which stays hoverable)
  // and the trigger stays perceivable via aria-disabled instead of the native
  // disabled attribute. Opening the picker is blocked by the isDisabled guards
  // in the handlers.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const status = statusProp ?? (validationError ? {
    type: 'error',
    message: validationError
  } : undefined);
  const {
    statusIcon,
    describedBy: statusTooltipDescribedBy
  } = useInputStatusIcon({
    status,
    statusVariant
  });

  // Required state. The trigger is a real button, but AT does not reliably
  // announce aria-required, so the trigger instead points its aria-describedby
  // at a visually hidden "Required" span — mirroring the Field label's visible
  // indicator (where isOptional takes precedence) and the pattern Slider uses
  // for its thumbs.
  const conveysRequired = isRequired && !isOptional;
  const ariaDescribedBy = [description ? descriptionID : null, statusVariant !== 'tooltip' && status?.message ? statusMessageID : null,
  // The tooltip variant renders no message box; describe the input by the
  // tooltip's content instead so the status is still announced.
  statusTooltipDescribedBy, conveysRequired ? requiredID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined;
  const defaultPlaceholder = isMultiple ? 'Choose files' : 'Choose file';
  const displayPlaceholder = placeholder ?? defaultPlaceholder;
  const handleFiles = useCallback(fileList => {
    if (isDisabled) {
      return;
    }
    const {
      valid,
      errors
    } = validateFiles(fileList, accept, maxSize, maxFiles, isMultiple);
    if (errors.length > 0) {
      setValidationError(errors[0]);
    } else {
      setValidationError(null);
    }
    if (valid.length === 0) {
      onChange(null);
      return;
    }
    const result = isMultiple ? valid : valid[0];
    onChange(result);

    // Announce the successful selection politely. Validation errors are
    // announced assertively by the FieldStatus that the derived error
    // status mounts, so only announce the attach here (do not
    // double-announce errors).
    if (errors.length === 0) {
      announce(valid.length === 1 ? `1 file selected: ${valid[0].name}` : `${valid.length} files selected`);
    }
    if (changeAction) {
      startTransition(async () => {
        await changeAction(result);
      });
    }
  }, [accept, isDisabled, isMultiple, maxFiles, maxSize, onChange, changeAction, startTransition, announce]);
  const handleInputChange = useCallback(e => {
    const fileList = Array.from(e.target.files ?? []);
    handleFiles(fileList);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [handleFiles]);
  const handleClear = useCallback(e => {
    e.stopPropagation();
    setValidationError(null);
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }, [onChange]);
  const handleClick = useCallback(() => {
    if (!isDisabled) {
      inputRef.current?.click();
    }
  }, [isDisabled]);

  // Explicit keyboard activation so Enter/Space open the picker even where a
  // synthetic keydown does not raise a native click. The isDisabled guard
  // blocks activation while focusable-disabled.
  const handleKeyDown = useCallback(e => {
    if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
      e.preventDefault();
      inputRef.current?.click();
    }
  }, [isDisabled]);

  // Make the surface clickable without giving it an interactive role. Clicks on
  // the nested clear/status controls are ignored by the hook, so those buttons
  // are plain siblings — no nested-interactive violation.
  const {
    onClick: onContainerClick,
    onMouseUp: onContainerMouseUp
  } = useClickableContainer({
    containerRef,
    onClick: handleClick,
    disabled: isDisabled
  });
  const handleDragEnter = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled && mode === 'dropzone') {
      setIsDragOver(true);
    }
  }, [isDisabled, mode]);
  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled && mode === 'dropzone') {
      setIsDragOver(true);
    }
  }, [isDisabled, mode]);
  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    // Moving over the dropzone's own children (icon, text) fires dragleave on
    // the container too — only a leave that actually exits the dropzone ends
    // the drag-over state, otherwise the highlight flickers mid-drag.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isDisabled || mode !== 'dropzone') {
      return;
    }
    const fileList = Array.from(e.dataTransfer.files);
    if (fileList.length > 0) {
      handleFiles(fileList);
    }
  }, [isDisabled, mode, handleFiles]);
  const hasFiles = value != null && (Array.isArray(value) ? value.length > 0 : true);
  const fileNames = hasFiles ? Array.isArray(value) ? value.map(f => f.name).join(', ') : value?.name ?? '' : null;
  const renderDropzoneContent = () => {
    if (isLoading) {
      return /*#__PURE__*/_jsx(Spinner, {
        size: "md"
      });
    }
    if (hasFiles) {
      return /*#__PURE__*/_jsx("div", {
        ...{
          className: "x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xb3r6kr xlyipyv x98rzlu xeuugli x2b8uid xeaf4i8"
        },
        children: fileNames
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: "arrowUp",
        size: "md",
        color: "secondary"
      }), /*#__PURE__*/_jsx("span", {
        ...{
          className: "x9ynric xjm74w1 x6pjikd xw6l6zx xv1l7n4 x2b8uid x87ps6o"
        },
        children: isDragOver ? 'Drop files here' : displayPlaceholder
      })]
    });
  };
  const renderCompactContent = () => {
    if (isLoading) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          ...{
            className: "x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xb3r6kr xlyipyv xuxw1ft x98rzlu xeuugli"
          },
          children: fileNames ?? displayPlaceholder
        }), /*#__PURE__*/_jsx(Spinner, {
          size: "sm"
        })]
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: "arrowUp",
        size: "sm",
        color: "secondary"
      }), /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x9ynric xjm74w1 x6pjikd xw6l6zx xv1l7n4 x87ps6o x1yc453h x98rzlu xeuugli"
          },
          2: {
            className: "x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xb3r6kr xlyipyv xuxw1ft x1yc453h x98rzlu xeuugli"
          },
          1: {
            className: "x9ynric xjm74w1 x6pjikd xw6l6zx xv1l7n4 x87ps6o x1yc453h"
          },
          3: {
            className: "x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xb3r6kr xlyipyv xuxw1ft x98rzlu xeuugli x1yc453h"
          }
        }[!!hasFiles << 1 | !!hasFiles << 0],
        children: fileNames ?? displayPlaceholder
      }), statusIcon]
    });
  };
  const isDropzone = mode === 'dropzone';
  const dragDropProps = isDropzone ? {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  } : {};
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    statusVariant: statusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        containerRef.current = el;
        // Anchor + hover/focus listeners for the disabled-message tooltip on
        // the visible container (the focusable trigger is visually hidden, so
        // anchoring there would place the tooltip at a 1px box). focusin
        // bubbles from the trigger button, so the tooltip still opens on
        // keyboard focus. Handlers are gated internally by isEnabled.
        disabledMessageTooltip.ref(el);
      },
      onClick: !isDisabled ? onContainerClick : undefined,
      onMouseUp: !isDisabled ? onContainerMouseUp : undefined,
      ...dragDropProps,
      ...mergeProps(themeProps('file-input', {
        mode,
        status: status?.type ?? null
      }), stylex.props(isDropzone ? styles.dropzone : styles.compact, isDropzone && !isDisabled && styles.dropzoneHover, isDropzone && isDragOver && styles.dropzoneActive, isDropzone && isDisabled && styles.dropzoneDisabled, !isDropzone && isDisabled && styles.compactDisabled, status && statusBorderStyles[status.type], xstyle), className, style),
      children: [/*#__PURE__*/_jsx(VisuallyHidden, {
        children: /*#__PURE__*/_jsx("button", {
          type: "button"
          // With a disabledMessage the trigger keeps focusability via
          // aria-disabled so the reason is focus-discoverable; opening the
          // picker is still blocked by the isDisabled guards in the handlers.
          // Otherwise the native disabled attribute removes it from the tab
          // order.
          ,
          disabled: isDisabled && !showsDisabledMessage,
          tabIndex: isDisabled && !showsDisabledMessage ? -1 : 0,
          "aria-disabled": showsDisabledMessage ? 'true' : undefined,
          onClick: handleClick,
          onKeyDown: handleKeyDown
          // aria-label suppresses child content from the accessible name, so
          // compose the selected filenames into it — otherwise a
          // screen-reader user refocusing the control hears only the field
          // label and cannot tell what (if anything) is attached.
          ,
          "aria-label": hasFiles && fileNames ? t('@astryx.fileInput.triggerWithFiles', {
            label,
            fileNames
          }) : label,
          "aria-busy": isLoading || undefined,
          "aria-describedby": ariaDescribedBy,
          "aria-invalid": status?.type === 'error' ? 'true' : undefined
        })
      }), /*#__PURE__*/_jsx("input", {
        ...rest,
        ref: mergeRefs(ref, inputRef),
        id: id,
        type: "file",
        accept: accept,
        multiple: isMultiple,
        disabled: isDisabled,
        onChange: handleInputChange
        // The visually-hidden native input is never focused (tabIndex={-1});
        // its describedby/required/invalid live on the trigger button.
        ,
        "aria-hidden": "true",
        tabIndex: -1,
        ...{
          className: "x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr xzpqnlu xuxw1ft xc342km"
        }
      }), isDropzone ? renderDropzoneContent() : renderCompactContent(), hasFiles && !isDisabled && !isLoading && /*#__PURE__*/_jsx(InputClearButton, {
        label: t('@astryx.fileInput.clearLabel', {
          label
        }),
        onClick: handleClear
      })]
    }), conveysRequired && /*#__PURE__*/_jsx(VisuallyHidden, {
      id: requiredID,
      children: t('@astryx.fileInput.required')
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
FileInput.displayName = 'FileInput';